import { useState, useEffect, useCallback } from 'react';
import { getLedOutputs, createLedOutput, updateLedOutput, deleteLedOutput, LedOutput, LedOutputInput } from '../../services/admin';
import { ledOutputLabel, sortLedOutputs } from './ledOutputs';

const emptyForm: LedOutputInput = { color: '', state: '', description: '', sort_order: 0 };

/** Global "LED Outputs" catalog (Settings > Model Catalog > "LED Outputs") - the model-independent
 * LED outputs (color + state, e.g. "Red / On") the platform can request. Each device model maps
 * the outputs it actually supports to its own OEM intent in the model editor's LEDs section; the
 * intent is never stored here. An output still mapped by a model cannot be deleted. */
export default function LedOutputsManager() {
  const [outputs, setOutputs] = useState<LedOutput[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<LedOutputInput>(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      setError(null);
      setOutputs(await getLedOutputs());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load LED outputs');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(() => {
      if (!showForm) load(true);
    }, 5000);
    return () => clearInterval(interval);
  }, [load, showForm]);

  const openAdd = () => {
    setEditingId(null);
    setForm({ ...emptyForm, sort_order: outputs.length + 1 });
    setShowForm(true);
  };

  const openEdit = (o: LedOutput) => {
    setEditingId(o.id);
    setForm({ color: o.color, state: o.state, description: o.description ?? '', sort_order: o.sort_order });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      if (editingId) {
        await updateLedOutput(editingId, form);
      } else {
        await createLedOutput(form);
      }
      closeForm();
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save LED output');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (o: LedOutput) => {
    if (!confirm(`Delete the "${ledOutputLabel(o)}" LED output?`)) return;
    try {
      await deleteLedOutput(o.id);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete LED output');
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-xl font-bold">💡 LED Outputs</h3>
          <p className="text-gray-400 text-sm mt-1">
            Global catalog of LED outputs (color + state). Each model maps the outputs it supports to its own intent.
          </p>
        </div>
        <button onClick={openAdd} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded">
          + Add LED Output
        </button>
      </div>

      {error && <div className="bg-red-900/30 border border-red-600 text-red-300 rounded-lg p-3">{error}</div>}

      <LedOutputsTable outputs={outputs} onEdit={openEdit} onDelete={handleDelete} />

      {showForm && (
        <div data-management-modal className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg border border-gray-700 w-full max-w-md p-6 space-y-4">
            <h3 className="text-2xl font-bold text-white">{editingId ? 'Edit LED Output' : 'Add LED Output'}</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <label className="block">
                  <p className="text-sm font-semibold text-gray-300 mb-1">Color</p>
                  <input
                    type="text"
                    value={form.color}
                    onChange={(e) => setForm({ ...form, color: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    placeholder="e.g., Red"
                    maxLength={50}
                    required
                  />
                </label>
                <label className="block">
                  <p className="text-sm font-semibold text-gray-300 mb-1">State</p>
                  <input
                    type="text"
                    value={form.state}
                    onChange={(e) => setForm({ ...form, state: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    placeholder="e.g., On"
                    maxLength={50}
                    required
                  />
                </label>
              </div>
              <label className="block">
                <p className="text-sm font-semibold text-gray-300 mb-1">Description</p>
                <textarea
                  value={form.description ?? ''}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={2}
                  maxLength={255}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                  placeholder="Optional"
                />
              </label>
              <div className="flex gap-2 justify-end pt-2">
                <button
                  type="button"
                  onClick={closeForm}
                  disabled={saving}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-white font-semibold rounded"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white font-semibold rounded"
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export function LedOutputsTable({
  outputs,
  onEdit,
  onDelete,
}: {
  outputs: LedOutput[];
  onEdit: (o: LedOutput) => void;
  onDelete: (o: LedOutput) => void;
}) {
  return (
    <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-gray-900/60 text-gray-400">
          <tr>
            <th className="px-4 py-3 text-left w-40">Output</th>
            <th className="px-4 py-3 text-left">Description</th>
            <th className="px-4 py-3 text-left w-28">Used by</th>
            <th className="px-4 py-3"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-700">
          {sortLedOutputs(outputs).map((o) => (
            <tr key={o.id} className="hover:bg-gray-700/30" data-led-output>
              <td className="px-4 py-3 font-mono font-semibold">{ledOutputLabel(o)}</td>
              <td className="px-4 py-3 text-gray-400">{o.description || '—'}</td>
              <td className="px-4 py-3 text-gray-400">
                {o.model_count === 1 ? '1 model' : `${o.model_count} models`}
              </td>
              <td className="px-4 py-3 text-right whitespace-nowrap">
                <button onClick={() => onEdit(o)} className="text-blue-400 hover:text-blue-300 font-semibold mr-3">
                  Edit
                </button>
                <button
                  onClick={() => onDelete(o)}
                  className="text-red-400 hover:text-red-300 font-semibold"
                  title={o.model_count > 0 ? 'Mapped by a model - remove those mappings first' : undefined}
                >
                  Delete
                </button>
              </td>
            </tr>
          ))}
          {outputs.length === 0 && (
            <tr>
              <td colSpan={4} className="px-4 py-6 text-center text-gray-400">
                No LED outputs yet - add one to get started.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
